import { useState } from "react";
import { MessageCircle, Phone, Mail, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { toast } from "sonner";
import {
  BUSINESS,
  WA_HREF,
  TEL_HREF,
  MAIL_HREF,
} from "../../lib/business";

const CATEGORIES = [
  "Healthcare",
  "Fitness",
  "Sport Nutrition",
  "Nutraceutical",
];

export const QuoteDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    category: "Nutraceutical",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildWA = () => {
    const lines = [
      "Hello White Bull Global, I'd like to request a quote.",
      "",
      `Name: ${form.name || "—"}`,
      `Company: ${form.company || "—"}`,
      `Phone: ${form.phone || "—"}`,
      `Email: ${form.email || "—"}`,
      `Category: ${form.category}`,
      "",
      "Requirement:",
      form.message || "—",
    ];
    return `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  const handleSendWhatsApp = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone number.");
      return;
    }
    window.open(buildWA(), "_blank", "noopener,noreferrer");
    setSubmitted(true);
    toast.success("Opening WhatsApp — we'll reply shortly.");
  };

  const handleClose = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => setSubmitted(false), 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="quote-dialog"
        className="max-w-lg rounded-none border border-[#E5E5E5] bg-white p-0 overflow-hidden"
      >
        <div className="p-6 md:p-8 border-b border-[#E5E5E5]">
          <DialogHeader className="space-y-2 text-left">
            <div className="overline">
              <span className="gold-divider mr-3 align-middle" />
              Request a quote
            </div>
            <DialogTitle className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A]">
              Tell us about your product.
            </DialogTitle>
            <DialogDescription className="text-[#595959] text-sm leading-relaxed">
              Share your requirement — we typically respond within one business
              day. For faster replies, send directly on WhatsApp.
            </DialogDescription>
          </DialogHeader>
        </div>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 border border-[#D4AF37] text-[#D4AF37] mx-auto">
              <Check className="w-7 h-7" />
            </div>
            <div className="font-serif-display text-2xl text-[#1A1A1A] mt-5">
              Message ready.
            </div>
            <p className="text-sm text-[#595959] mt-2">
              We&apos;ve opened WhatsApp with your details filled in — just hit
              send.
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSendWhatsApp}
            className="p-6 md:p-8 space-y-4"
            data-testid="quote-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="q-name" className="text-xs tracking-wider uppercase text-[#595959]">
                  Your name *
                </Label>
                <Input
                  id="q-name"
                  data-testid="quote-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  required
                  className="mt-1 rounded-none border-[#E5E5E5] focus-visible:ring-[#D4AF37] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37] h-11"
                />
              </div>
              <div>
                <Label htmlFor="q-company" className="text-xs tracking-wider uppercase text-[#595959]">
                  Company
                </Label>
                <Input
                  id="q-company"
                  data-testid="quote-company"
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                  className="mt-1 rounded-none border-[#E5E5E5] focus-visible:ring-[#D4AF37] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37] h-11"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="q-phone" className="text-xs tracking-wider uppercase text-[#595959]">
                  Phone *
                </Label>
                <Input
                  id="q-phone"
                  data-testid="quote-phone"
                  value={form.phone}
                  type="tel"
                  onChange={(e) => update("phone", e.target.value)}
                  required
                  className="mt-1 rounded-none border-[#E5E5E5] focus-visible:ring-[#D4AF37] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37] h-11"
                />
              </div>
              <div>
                <Label htmlFor="q-email" className="text-xs tracking-wider uppercase text-[#595959]">
                  Email
                </Label>
                <Input
                  id="q-email"
                  data-testid="quote-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="mt-1 rounded-none border-[#E5E5E5] focus-visible:ring-[#D4AF37] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37] h-11"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs tracking-wider uppercase text-[#595959]">
                Category
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    type="button"
                    key={c}
                    data-testid={`quote-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => update("category", c)}
                    className={`px-4 h-9 text-xs tracking-wider uppercase border transition-colors ${
                      form.category === c
                        ? "bg-[#1A1A1A] border-[#1A1A1A] text-white"
                        : "bg-white border-[#E5E5E5] text-[#2A2A2A] hover:border-[#D4AF37]"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="q-msg" className="text-xs tracking-wider uppercase text-[#595959]">
                Requirement
              </Label>
              <Textarea
                id="q-msg"
                data-testid="quote-message"
                rows={4}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Product type, target volume, MOQ, any specific formulation details…"
                className="mt-1 rounded-none border-[#E5E5E5] focus-visible:ring-[#D4AF37] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]"
              />
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
              <Button
                type="submit"
                data-testid="quote-submit"
                className="btn-gold rounded-none h-11 px-6 text-sm font-semibold tracking-wider inline-flex items-center gap-2 w-full sm:w-auto"
              >
                <MessageCircle className="w-4 h-4" />
                Send via WhatsApp
              </Button>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <a
                  data-testid="quote-call-alt"
                  href={TEL_HREF}
                  className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 border border-[#1A1A1A] text-[#1A1A1A] text-xs uppercase tracking-wider hover:bg-[#1A1A1A] hover:text-white transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  Call
                </a>
                <a
                  data-testid="quote-email-alt"
                  href={MAIL_HREF}
                  className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 border border-[#1A1A1A] text-[#1A1A1A] text-xs uppercase tracking-wider hover:bg-[#1A1A1A] hover:text-white transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </a>
              </div>
            </DialogFooter>

            <p className="text-[11px] text-[#8a8a8a] pt-1">
              By submitting, a pre-filled WhatsApp message will open so you can
              send it directly to our team.
            </p>
          </form>
        )}

        <a
          href={WA_HREF}
          target="_blank"
          rel="noreferrer"
          data-testid="quote-direct-whatsapp"
          className="block text-center py-3 border-t border-[#E5E5E5] text-xs tracking-wider uppercase text-[#2A2A2A] hover:text-[#D4AF37] transition-colors"
        >
          Or message us directly on WhatsApp →
        </a>
      </DialogContent>
    </Dialog>
  );
};
